import { Component, type ErrorInfo, type ReactNode } from "react";
import { recordError } from "@/lib/crashlytics";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * App-wide error boundary.
 *
 * A render-time exception anywhere in the tree (e.g. a screen reading injected
 * window data the wrong way) used to unmount React to a fully blank page with
 * no way back. This catches that, shows a recoverable message, and lets the
 * member retry or return home instead of being stuck on a white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the crash in logs/telemetry so a blank-screen bug can't hide.
    console.error("Unhandled UI error:", error, info.componentStack);
    // Non-fatal report to Firebase Crashlytics on native (no-op on web).
    recordError(error, {
      source: "ErrorBoundary",
      componentStack: (info.componentStack ?? "").slice(0, 1000),
    });
  }

  private handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.assign("/dashboard");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          padding: "24px",
          textAlign: "center",
          background: "#F5F0EB",
          color: "#2b2b2b",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "20px", margin: 0 }}>Something went wrong</h1>
        <p style={{ maxWidth: 360, margin: 0, opacity: 0.75, lineHeight: 1.5 }}>
          That screen ran into an error. You can head back and try again — your
          account and bookings are safe.
        </p>
        <button
          onClick={this.handleReload}
          style={{
            marginTop: 8,
            padding: "12px 22px",
            borderRadius: 999,
            border: "none",
            background: "#2b2b2b",
            color: "#fff",
            fontSize: "15px",
            cursor: "pointer",
          }}
        >
          Back to home
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
